import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2025 semantics GmbH
//  Purpose   : viewer controls
// =============================================================================
// ---Third-party imports
import React, { useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
// ---Local imports
import styles from './viewer.scss';
// ---Helpers
const clamp = (value, min, max)=>Math.min(Math.max(value, min), max);
const getSafeRange = (min, max)=>{
    let safeMin = Math.max(min, 1);
    let safeMax = Math.max(max, safeMin * 1.01);
    return [
        safeMin,
        safeMax
    ];
};
const fractionFromZoom = (zoom, min, max)=>{
    if (max <= min) return 0;
    let value = clamp(zoom, min, max);
    let logMin = Math.log(min);
    let logMax = Math.log(max);
    let logValue = Math.log(value);
    return (logValue - logMin) / (logMax - logMin);
};
const zoomFromFraction = (fraction, min, max)=>{
    if (max <= min) return min;
    let clamped = clamp(fraction, 0, 1);
    let logMin = Math.log(min);
    let logMax = Math.log(max);
    return Math.exp(logMin + clamped * (logMax - logMin));
};
const nextZoom = ({ zoom, zoomMin, zoomMax, direction })=>{
    let base = Math.max(zoom, zoomMin || 1);
    let delta = Math.max(1, (base / 20) ** 1.4);
    let next = direction > 0 ? zoom + delta : zoom - delta;
    return clamp(next, zoomMin, zoomMax);
};
const stopPointerEvent = (event)=>event.stopPropagation();
// ---Component
export const ZoomControl = ({ mode, zoom, zoomMin, zoomMax, setZoom, className, style, renderLabel })=>{
    let controlMode = mode === true ? 'button' : mode;
    let showButtons = controlMode === 'button' || controlMode === 'buttonSlider';
    let showSlider = controlMode === 'slider' || controlMode === 'buttonSlider';
    if (!showButtons && !showSlider) return null;
    let [safeMin, safeMax] = useMemo(()=>getSafeRange(zoomMin, zoomMax), [
        zoomMin,
        zoomMax
    ]);
    let sliderFraction = useMemo(()=>fractionFromZoom(zoom, safeMin, safeMax), [
        zoom,
        safeMin,
        safeMax
    ]);
    let sliderRef = useRef(null);
    let setZoomFromFraction = useCallback((fraction)=>{
        let next = zoomFromFraction(fraction, safeMin, safeMax);
        setZoom(clamp(next, zoomMin, zoomMax));
    }, [
        safeMin,
        safeMax,
        zoomMin,
        zoomMax,
        setZoom
    ]);
    let handleZoomIn = useCallback((event)=>{
        event.stopPropagation();
        event.preventDefault();
        setZoom(nextZoom({
            zoom,
            zoomMin,
            zoomMax,
            direction: 1
        }));
    }, [
        zoom,
        zoomMin,
        zoomMax,
        setZoom
    ]);
    let handleZoomOut = useCallback((event)=>{
        event.stopPropagation();
        event.preventDefault();
        setZoom(nextZoom({
            zoom,
            zoomMin,
            zoomMax,
            direction: -1
        }));
    }, [
        zoom,
        zoomMin,
        zoomMax,
        setZoom
    ]);
    let handleSliderPointerDown = useCallback((event)=>{
        if (!sliderRef.current) return;
        event.stopPropagation();
        event.preventDefault();
        let element = sliderRef.current;
        let updateFromClientY = (clientY)=>{
            if (!element) return;
            let rect = element.getBoundingClientRect();
            if (rect.height === 0) return;
            let fraction = 1 - (clientY - rect.top) / rect.height;
            setZoomFromFraction(clamp(fraction, 0, 1));
        };
        updateFromClientY(event.clientY);
        let pointerId = event.pointerId;
        let pointerCaptured = false;
        if (element.setPointerCapture) {
            try {
                element.setPointerCapture(pointerId);
                pointerCaptured = true;
            } catch (_) {
                pointerCaptured = false;
            }
        }
        let moveTarget = element;
        if (!pointerCaptured && typeof window !== 'undefined') {
            moveTarget = window;
        }
        let handlePointerMove = (moveEvent)=>updateFromClientY(moveEvent.clientY);
        let endInteraction = ()=>{
            if (!element) return;
            if (pointerCaptured && element.releasePointerCapture) {
                try {
                    element.releasePointerCapture(pointerId);
                } catch (_) {
                // ignore
                }
            }
            moveTarget.removeEventListener('pointermove', handlePointerMove);
            moveTarget.removeEventListener('pointerup', endInteraction);
            moveTarget.removeEventListener('pointercancel', endInteraction);
        };
        moveTarget.addEventListener('pointermove', handlePointerMove);
        moveTarget.addEventListener('pointerup', endInteraction);
        moveTarget.addEventListener('pointercancel', endInteraction);
    }, [
        setZoomFromFraction
    ]);
    let handleSliderKeyDown = useCallback((event)=>{
        switch(event.key){
            case 'ArrowUp':
            case 'ArrowRight':
                event.preventDefault();
                setZoom(nextZoom({
                    zoom,
                    zoomMin,
                    zoomMax,
                    direction: 1
                }));
                break;
            case 'ArrowDown':
            case 'ArrowLeft':
                event.preventDefault();
                setZoom(nextZoom({
                    zoom,
                    zoomMin,
                    zoomMax,
                    direction: -1
                }));
                break;
            case 'PageUp':
                event.preventDefault();
                setZoomFromFraction(clamp(sliderFraction + 0.1, 0, 1));
                break;
            case 'PageDown':
                event.preventDefault();
                setZoomFromFraction(clamp(sliderFraction - 0.1, 0, 1));
                break;
            case 'Home':
                event.preventDefault();
                setZoom(clamp(zoomMin, zoomMin, zoomMax));
                break;
            case 'End':
                event.preventDefault();
                setZoom(clamp(zoomMax, zoomMin, zoomMax));
                break;
            default:
                break;
        }
    }, [
        setZoom,
        setZoomFromFraction,
        sliderFraction,
        zoom,
        zoomMin,
        zoomMax
    ]);
    let label = null;
    if (renderLabel) {
        label = /*#__PURE__*/ _jsx("div", {
            className: styles.zoomControlLabel,
            "aria-live": "polite",
            children: renderLabel({
                zoom,
                zoomMin,
                zoomMax
            })
        });
    }
    let slider = null;
    if (showSlider) {
        slider = /*#__PURE__*/ _jsx("div", {
            className: styles.zoomControlSliderBox,
            children: /*#__PURE__*/ _jsxs("div", {
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                ref: sliderRef,
                role: "slider",
                tabIndex: 0,
                "aria-label": "Zoom level",
                "aria-orientation": "vertical",
                "aria-valuemin": Math.round(zoomMin),
                "aria-valuemax": Math.round(zoomMax),
                "aria-valuenow": Math.round(clamp(zoom, zoomMin, zoomMax)),
                className: styles.zoomControlSliderTrack,
                onPointerDown: handleSliderPointerDown,
                onKeyDown: handleSliderKeyDown,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.zoomControlSliderFill,
                        style: {
                            height: `${sliderFraction * 100}%`
                        }
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.zoomControlSliderHandle,
                        style: {
                            bottom: `${sliderFraction * 100}%`
                        }
                    })
                ]
            })
        });
    }
    let zoomInButton = (extraClass)=>/*#__PURE__*/ _jsx("button", {
            type: "button",
            className: clsx(styles.zoomControlButton, extraClass),
            onClick: handleZoomIn,
            title: "Zoom in",
            "aria-label": "Zoom in",
            disabled: zoom >= zoomMax,
            children: "+"
        });
    let zoomOutButton = (extraClass)=>/*#__PURE__*/ _jsx("button", {
            type: "button",
            className: clsx(styles.zoomControlButton, extraClass),
            onClick: handleZoomOut,
            title: "Zoom out",
            "aria-label": "Zoom out",
            disabled: zoom <= zoomMin,
            children: "-"
        });
    let content = null;
    if (showButtons && showSlider) {
        content = /*#__PURE__*/ _jsxs("div", {
            className: styles.zoomControlButtonSlider,
            children: [
                zoomInButton(styles.zoomControlButtonTop),
                slider,
                zoomOutButton(styles.zoomControlButtonBottom)
            ]
        });
    } else {
        content = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                showButtons && /*#__PURE__*/ _jsxs("div", {
                    className: styles.zoomControlButtons,
                    children: [
                        zoomInButton(),
                        zoomOutButton()
                    ]
                }),
                showSlider && !showButtons ? /*#__PURE__*/ _jsx("div", {
                    className: styles.zoomControlButtonSlider,
                    children: slider
                }) : slider
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: clsx(styles.zoomControl, {
            [styles.zoomControlHasSlider]: showSlider,
            [styles.zoomControlHasButtons]: showButtons,
            [styles.zoomControlHasButtonSlider]: showButtons && showSlider
        }, className),
        style: style,
        onPointerDown: stopPointerEvent,
        onMouseDown: stopPointerEvent,
        onWheel: stopPointerEvent,
        onClick: stopPointerEvent,
        children: [
            content,
            label
        ]
    });
};
ZoomControl.propTypes = {
    mode: PropTypes.oneOf([
        false,
        true,
        'button',
        'slider',
        'buttonSlider'
    ]),
    zoom: PropTypes.number.isRequired,
    zoomMin: PropTypes.number.isRequired,
    zoomMax: PropTypes.number.isRequired,
    setZoom: PropTypes.func.isRequired,
    className: PropTypes.string,
    style: PropTypes.object,
    renderLabel: PropTypes.func
};
ZoomControl.defaultProps = {
    mode: 'button',
    className: null,
    style: null,
    renderLabel: null
};
